import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { Container, Row, Col, Card, CardBody, Button, Badge } from 'reactstrap';
import { ChartSkeleton } from './SkeletonLoader';
import { Line, Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Title,
  SubTitle,
  Tooltip,
  Legend,
  Filler,
  Plugin,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { candlestickPlugin } from '../utils/candlestickPlugin';
import { FaArrowLeft, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import requests from '../requests';
import { PATTERN_INFO } from '../utils/patternInfo';
import { PatternSparkline } from './PatternSparkline';
import ShareButton from './ShareButton';
import EarningsBadge from './EarningsBadge';
import { useFundamentals, formatMarketCap } from '../hooks/useFundamentals';
import { useTickerNames } from '../hooks/useTickerNames';
import { useSeo } from '../hooks/useSeo';
import { buildDashboardUrl } from '../utils/links';
import { PEAK_KEY_POINTS, TROUGH_KEY_POINTS } from '../utils/patternKeyPoints';
import './PatternDetail.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Title,
  SubTitle,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin
);

// candlestickPlugin (and its OHLCPoint/CandleDataset payload types) now lives
// in `utils/candlestickPlugin` — shared with `MatchCandlestickChart` (Query
// Matcher match-card candlestick view) so both charts render OHLC bars
// identically. See that module's doc comment for the "why" behind the
// invisible-bar-dataset + afterDatasetsDraw approach.

// PEAK_KEY_POINTS/TROUGH_KEY_POINTS (which key-point names get a candle-mode triangle,
// and which orientation) now live in utils/patternKeyPoints.ts — shared with the
// homepage's compact pattern-signal chart (HomeEarningsSignals).

interface MACDData { macd: (number | null)[]; signal: (number | null)[]; histogram: (number | null)[]; }
interface BBandsData { upper: (number | null)[]; middle: (number | null)[]; lower: (number | null)[]; }
interface StochData { k: (number | null)[]; d: (number | null)[]; }
interface SRLevel { price: number; touches: number; first_touch: string | null; last_touch: string | null; }

export function PatternDetail() {
  const getFundamentals = useFundamentals();
  const getTickerName = useTickerNames();
  const { ticker, patternType, startDate } = useParams<Record<string, string>>();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [searchParams] = useSearchParams();
  const [pattern, setPattern] = useState<PatternMatch | null>(null);
  const [fullPriceData, setFullPriceData] = useState<number[]>([]);
  const [priceDates, setPriceDates] = useState<string[]>([]);
  const [patternStartIndex, setPatternStartIndex] = useState<number>(0);
  const [patternEndIndex, setPatternEndIndex] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ohlcOpens, setOhlcOpens] = useState<(number | null)[]>([]);
  const [ohlcHighs, setOhlcHighs] = useState<(number | null)[]>([]);
  const [ohlcLows,  setOhlcLows]  = useState<(number | null)[]>([]);
  const [chartMode, setChartMode] = useState<'line' | 'candle'>('candle');
  const [candleDataAvailable, setCandleDataAvailable] = useState(false);
  const [rsiData, setRsiData] = useState<(number | null)[]>([]);
  const [showRsi, setShowRsi] = useState(true);
  const [rsiLoading, setRsiLoading] = useState(false);
  const [macdData, setMacdData] = useState<MACDData | null>(null);
  const [showMacd, setShowMacd] = useState(false);
  const [bbandsData, setBbandsData] = useState<BBandsData | null>(null);
  const [showBbands, setShowBbands] = useState(false);
  const [stochData, setStochData] = useState<StochData | null>(null);
  const [showStoch, setShowStoch] = useState(false);
  const [srData, setSrData] = useState<SRLevel[]>([]);
  // Overlaid directly on the existing main chart (no extra sub-panel, unlike RSI/MACD/
  // Stoch/Volume) — lightweight enough to default on.
  const [showSr, setShowSr] = useState(true);
  const [volumeData, setVolumeData] = useState<(number | null)[]>([]);
  const [showVolume, setShowVolume] = useState(false);
  const [indicatorDropdownOpen, setIndicatorDropdownOpen] = useState(false);
  const indicatorDropdownRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mainChartRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rsiChartRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const macdChartRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stochChartRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const volumeChartRef = useRef<any>(null);

  // Per-route SEO. Without this, every /patterns/<ticker>/<type>/<date> page inherited
  // index.html's homepage <title>/canonical, so Google filed ~660 of them as "Duplicate
  // without user-selected canonical" (GSC Aug 2026). useSeo's canonical defaults to the
  // query-less pathname, so the ?sentiment= back-navigation variants (PatternCard links)
  // collapse onto the clean detail URL. Values fall back to the route params while the
  // pattern is still loading so the tags are distinct on first paint too.
  const seoTicker = (pattern?.ticker ?? ticker ?? '').toUpperCase();
  const seoName =
    pattern?.pattern_display_name ??
    (patternType ? patternType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Chart Pattern');
  const seoSentiment = pattern?.sentiment && pattern.sentiment !== 'neutral' ? ` (${pattern.sentiment})` : '';
  const seoMeta = pattern ? PATTERN_INFO[pattern.pattern_type] : undefined;
  const seoTitle = `${seoTicker} ${seoName}${seoSentiment} chart pattern${startDate ? ` — ${startDate}` : ''} | DejaChart`;
  const seoDesc =
    `${seoTicker} formed a ${seoName.toLowerCase()} pattern` +
    (pattern ? ` at ${Math.round(pattern.confidence * 100)}% confidence` : '') +
    `. ${seoMeta?.summary ?? ''} See the chart, formation range, and what historically followed on DejaChart.`;
  useSeo({ title: seoTitle, description: seoDesc });

  const windowParam = searchParams.get('window');
  const lookbackParam = searchParams.get('lookback_days');
  // Per-signal unlock token from a social share link. Largely vestigial since the
  // 2026-08-11 flip (no pattern window is Pro-gated any more); still forwarded so old
  // shared links keep working while the token machinery is scheduled for removal.
  const unlockParam = searchParams.get('unlock');

  useEffect(() => {
    const fetchPattern = async () => {
      try {
        setLoading(true);
        setError(null);
        // Use min_confidence=0 to get all patterns regardless of confidence
        const windowQuery = windowParam ? `&window=${windowParam}` : '';
        const lookbackQuery = lookbackParam ? `&lookback_days=${lookbackParam}` : '';
        const unlockQuery = unlockParam ? `&unlock=${encodeURIComponent(unlockParam)}` : '';
        const response = await requests.get(`/api/patterns?ticker=${ticker}&pattern_type=${patternType}&min_confidence=0&limit=200${windowQuery}${lookbackQuery}${unlockQuery}`);


        // Find the specific pattern by start_date. The same pattern can drift by
        // ~a day between detection runs, so the link's start_date (from the list /
        // share endpoint) may not exactly match what this query returns — fall back
        // to the nearest start_date (same window) within a few days.
        const patterns: PatternMatch[] = response.data.patterns ?? [];
        const targetMs = startDate ? new Date(`${startDate}T00:00:00`).getTime() : NaN;
        const TOLERANCE_MS = 5 * 24 * 60 * 60 * 1000;
        const exact = patterns.find((p) => p.start_date === startDate);
        const nearest = exact
          ? undefined
          : patterns
            .filter((p) => !windowParam || String(p.window) === windowParam)
            .map((p) => ({ p, d: Math.abs(new Date(`${p.start_date}T00:00:00`).getTime() - targetMs) }))
            .filter((x) => !Number.isNaN(x.d) && x.d <= TOLERANCE_MS)
            .sort((a, b) => a.d - b.d)[0]?.p;
        const foundPattern = exact ?? nearest;

        if (foundPattern) {
          setPattern(foundPattern);
          posthog.capture("pattern_detail_viewed", {
            ticker,
            pattern_type: patternType,
            start_date: startDate,
            confidence: foundPattern.confidence,
            window: foundPattern.window,
          });

          // Fetch price history with context around the pattern
          const contextResponse = await requests.get(
            `/api/patterns/price-context?ticker=${ticker}&start_date=${foundPattern.start_date}&end_date=${foundPattern.end_date}&context_days=10`
          );

          const contextData = contextResponse.data;
          setFullPriceData(contextData.prices ?? []);
          setPriceDates(contextData.dates ?? []);
          setPatternStartIndex(contextData.pattern_start_index ?? 0);
          setPatternEndIndex(contextData.pattern_end_index ?? 0);
          setOhlcOpens(contextData.opens   ?? []);
          setOhlcHighs(contextData.highs   ?? []);
          setOhlcLows(contextData.lows     ?? []);
          setVolumeData(contextData.volumes ?? []);
          const nonNull = (contextData.opens ?? []).filter((v: number | null) => v !== null).length;
          setCandleDataAvailable(nonNull / ((contextData.opens?.length || 1)) >= 0.8);
        } else {
          setError('Pattern not found');
        }
      } catch (err) {
        setError('Failed to load pattern details');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (ticker && patternType && startDate) {
      fetchPattern();
    }
  }, [ticker, patternType, startDate, windowParam, lookbackParam, unlockParam, posthog]);

  useEffect(() => {
    if (!pattern) return;
    setRsiLoading(true);
    fetch(`/api/indicators?ticker=${ticker}&start_date=${pattern.start_date}&end_date=${pattern.end_date}&context_days=10&type=rsi,macd,bbands,stoch,sr`)
      .then(r => r.json())
      .then(data => {
        const ind = data.indicators ?? {};
        setRsiData(ind.rsi ?? []);
        if (ind.macd) setMacdData(ind.macd);
        if (ind.bbands) setBbandsData(ind.bbands);
        if (ind.stoch) setStochData(ind.stoch);
        setSrData(ind.sr ?? []);
      })
      .finally(() => setRsiLoading(false));
  }, [pattern, ticker]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (indicatorDropdownRef.current && !indicatorDropdownRef.current.contains(e.target as Node)) {
        setIndicatorDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // All sub-panel refs — captured once (refs are stable objects, .current is read at event time)
  const subPanelRefs = [rsiChartRef, macdChartRef, stochChartRef, volumeChartRef];

  const syncHoverPlugin = useMemo<Plugin>(() => ({
    id: 'syncHover',
    afterEvent(chart: ChartJS, args: { event: { type: string } }) {
      const isMain = chart === mainChartRef.current;
      const others: ChartJS[] = isMain
        ? subPanelRefs.map(r => r.current).filter(Boolean)
        : [mainChartRef.current].filter(Boolean);
      if (!others.length) return;

      if (args.event.type === 'mouseout') {
        others.forEach(other => {
          other.tooltip?.setActiveElements([], { x: 0, y: 0 });
          other.update('none');
        });
        return;
      }
      const active = chart.tooltip?.getActiveElements() ?? [];
      if (!active.length) return;
      const idx = active[0].index;
      others.forEach(other => {
        if (!other.getDatasetMeta(0).data[idx]) return;
        other.tooltip?.setActiveElements([{ datasetIndex: 0, index: idx }], { x: 0, y: 0 });
        other.update('none');
      });
    },
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Container className="pattern-detail-loading py-5">
        <ChartSkeleton />
      </Container>
    );
  }

  // Build back URL preserving filters from URL params
  const getBackUrl = () => {
    const params = new URLSearchParams();
    const sentiment = searchParams.get('sentiment');
    if (sentiment) {
      params.set('sentiment', sentiment);
    } else if (pattern?.sentiment) {
      params.set('sentiment', pattern.sentiment);
    }
    const view = searchParams.get('view');
    if (view) {
      params.set('view', view);
    }
    const types = searchParams.get('types');
    if (types) {
      params.set('types', types);
    }
    const qs = params.toString();
    return `/patterns${qs ? `?${qs}` : ''}`;
  };

  if (error || !pattern) {
    return (
      <Container className="pattern-detail-error">
        <h3>{error || 'Pattern not found'}</h3>
        <Button color="primary" onClick={() => navigate('/patterns')}>
          Back to Patterns
        </Button>
      </Container>
    );
  }

  // Highlight the pattern window within the context
  const getPatternAnnotations = () => {
    if (fullPriceData.length === 0) {
      return {};
    }

    // Build a point annotation using candle high/low anchors + triangle markers in candle mode,
    // falling back to close-price circles in line mode.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const makePointAnnotation = (pointName: string, absoluteIndex: number, label: string): any => {
      const isPeak   = PEAK_KEY_POINTS.has(pointName);
      const isTrough = TROUGH_KEY_POINTS.has(pointName);

      let yValue    = fullPriceData[absoluteIndex];
      let pointStyle: string = 'circle';
      let rotation  = 0;
      let yAdjust   = -10;

      if (chartMode === 'candle') {
        if (isPeak && ohlcHighs[absoluteIndex] !== null) {
          yValue    = ohlcHighs[absoluteIndex] as number;
          pointStyle = 'triangle';
          rotation  = 180;  // ▼ above the high wick
          yAdjust   = -10;
        } else if (isTrough && ohlcLows[absoluteIndex] !== null) {
          yValue    = ohlcLows[absoluteIndex] as number;
          pointStyle = 'triangle';
          rotation  = 0;    // ▲ below the low wick
          yAdjust   = 10;
        }
      }

      return {
        type: 'point',
        xValue: absoluteIndex,
        yValue,
        pointStyle,
        rotation,
        backgroundColor: 'rgba(255, 99, 132, 0.8)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 2,
        radius: 8,
        label: {
          display: true,
          content: label,
          position: 'top',
          backgroundColor: 'rgba(255, 99, 132, 0.9)',
          color: 'white',
          font: { size: 10, weight: 'bold' },
          padding: 4,
          yAdjust,
        },
      };
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const annotations: Record<string, any> = {
      patternHighlight: {
        type: 'box',
        xMin: patternStartIndex,
        xMax: patternEndIndex,
        backgroundColor: 'rgba(99, 132, 255, 0.15)',
        borderColor: 'rgba(99, 132, 255, 0.5)',
        borderWidth: 1,
        borderDash: [4, 4],
        label: {
          display: true,
          content: 'Pattern Window',
          position: 'start',
          backgroundColor: 'rgba(99, 132, 255, 0.7)',
          color: 'white',
          font: {
            size: 11,
            weight: 'bold',
          },
        },
      },
    };

    // Add key point annotations if available
    if (pattern.key_points) {
      const keyPointLabels: { [key: string]: string } = {
        // Head and Shoulders
        initial_low: 'Initial Low',
        left_shoulder: 'Left Shoulder',
        left_trough: 'Left Trough',
        head: 'Head',
        right_trough: 'Right Trough',
        right_shoulder: 'Right Shoulder',
        neckline_break: 'Neckline Break',
        // Inverse Head and Shoulders
        initial_high: 'Initial High',
        left_peak: 'Left Peak',
        right_peak: 'Right Peak',
        // Cup and Handle
        left_rim: 'Left Rim',
        cup_left: 'Cup Left',
        cup_bottom: 'Cup Bottom',
        cup_right: 'Cup Right',
        pre_handle: 'Pre-Handle',
        handle_dip: 'Handle Dip',
        breakout: 'Breakout',
        // Double Top/Bottom
        first_peak: 'First Peak',
        first_bottom: 'First Bottom',
        valley: 'Valley',
        peak: 'Peak',
        second_peak: 'Second Peak',
        second_bottom: 'Second Bottom',
        breakdown: 'Breakdown',
        // Triangles
        first_low: 'First Low',
        first_high: 'First High',
        second_low: 'Second Low',
        second_high: 'Second High',
        third_low: 'Third Low',
        third_high: 'Third High',
        // Triple Top/Bottom
        first_ascent: 'First Ascent',
        first_descent: 'First Descent',
        valley_1: 'Valley 1',
        second_ascent: 'Second Ascent',
        second_descent: 'Second Descent',
        valley_2: 'Valley 2',
        third_ascent: 'Third Ascent',
        third_peak: 'Third Peak',
        third_descent: 'Third Descent',
        third_bottom: 'Third Bottom',
        peak_1: 'Peak 1',
        peak_2: 'Peak 2',
        // Rounding Bottom/Top
        left_quarter: 'Left Quarter',
        bottom: 'Bottom',
        right_quarter: 'Right Quarter',
        right_rim: 'Right Rim',
        top: 'Top',
        // Broadening Formation / Symmetrical Triangle
        start: 'Start',
        apex_low: 'Apex',
        // Flags / Pennants
        pole_start: 'Pole Start',
        pole_top: 'Pole Top',
        pole_bottom: 'Pole Bottom',
        flag_high: 'Flag High',
        flag_low: 'Flag Low',
        pennant_high_1: 'Pennant High 1',
        pennant_low_1: 'Pennant Low 1',
        pennant_high_2: 'Pennant High 2',
        pennant_low_2: 'Pennant Low 2',
        // Inverse Cup and Handle
        arch_left: 'Arch Left',
        arch_top: 'Arch Top',
        arch_right: 'Arch Right',
        handle_peak: 'Handle Peak',
      };

      // Special handling for cup and handle pattern - draw cup curve
      if (pattern.pattern_type === 'cup_handle' && 'left_rim' in pattern.key_points && 'cup_bottom' in pattern.key_points && 'cup_right' in pattern.key_points) {
        const leftRimIdx = patternStartIndex + pattern.key_points.left_rim;
        const cupRightIdx = patternStartIndex + pattern.key_points.cup_right;

        // Highlight the cup region with a box
        annotations['cup_region'] = {
          type: 'box',
          xMin: leftRimIdx,
          xMax: cupRightIdx,
          backgroundColor: 'rgba(54, 162, 235, 0.15)',
          borderColor: 'rgba(54, 162, 235, 0.6)',
          borderWidth: 2,
          borderDash: [5, 5],
          label: {
            display: true,
            content: 'Cup Formation',
            position: 'start',
            backgroundColor: 'rgba(54, 162, 235, 0.9)',
            color: 'white',
            font: {
              size: 11,
              weight: 'bold',
            },
          },
        };

        // Note: Point annotations removed for cleaner visualization
        // Cup-and-handle patterns use boxes only (cup region and handle region)

        // Handle portion - highlight as a box region
        // Handle should start right after cup ends (cup_right) and extend to breakout
        {
          const handleStartIdx = cupRightIdx; // Start where cup ends

          // Handle extends to breakout (or pattern end if no breakout)
          let handleEndIdx = patternEndIndex;
          if ('breakout' in pattern.key_points) {
            handleEndIdx = patternStartIndex + pattern.key_points.breakout;
          }

          annotations['handle_region'] = {
            type: 'box',
            xMin: handleStartIdx,
            xMax: handleEndIdx,
            backgroundColor: 'rgba(255, 159, 64, 0.15)',
            borderColor: 'rgba(255, 159, 64, 0.6)',
            borderWidth: 2,
            borderDash: [3, 3],
            label: {
              display: true,
              content: 'Handle',
              position: 'start',
              backgroundColor: 'rgba(255, 159, 64, 0.9)',
              color: 'white',
              font: {
                size: 11,
                weight: 'bold',
              },
            },
          };
        }
      } else if (pattern.pattern_type === 'triple_top') {
        // Triple top: show 3 peaks + 2 valleys for structural context
        const keyPointsToShow = ['first_peak', 'valley_1', 'second_peak', 'valley_2', 'third_peak'];

        Object.entries(pattern.key_points).forEach(([pointName, relativeIndex]) => {
          if (!keyPointsToShow.includes(pointName)) return;
          const absoluteIndex = patternStartIndex + relativeIndex;
          if (absoluteIndex >= 0 && absoluteIndex < fullPriceData.length) {
            const label = keyPointLabels[pointName] || pointName.replace(/_/g, ' ');
            annotations[`keypoint_${pointName}`] = makePointAnnotation(pointName, absoluteIndex, label);
          }
        });
      } else if (pattern.pattern_type === 'triple_bottom') {
        // Triple bottom: show 3 bottoms + 2 peaks for structural context
        const keyPointsToShow = ['first_bottom', 'peak_1', 'second_bottom', 'peak_2', 'third_bottom'];

        Object.entries(pattern.key_points).forEach(([pointName, relativeIndex]) => {
          if (!keyPointsToShow.includes(pointName)) return;
          const absoluteIndex = patternStartIndex + relativeIndex;
          if (absoluteIndex >= 0 && absoluteIndex < fullPriceData.length) {
            const label = keyPointLabels[pointName] || pointName.replace(/_/g, ' ');
            annotations[`keypoint_${pointName}`] = makePointAnnotation(pointName, absoluteIndex, label);
          }
        });
      } else if (pattern.pattern_type === 'inv_cup_handle' && 'arch_top' in pattern.key_points && 'arch_right' in pattern.key_points) {
        // Inverse cup and handle: use box regions (mirror of cup_handle display)
        const leftRimIdx = patternStartIndex + (pattern.key_points.left_rim || 0);
        const archRightIdx = patternStartIndex + pattern.key_points.arch_right;

        annotations['arch_region'] = {
          type: 'box',
          xMin: leftRimIdx,
          xMax: archRightIdx,
          backgroundColor: 'rgba(255, 99, 132, 0.15)',
          borderColor: 'rgba(255, 99, 132, 0.6)',
          borderWidth: 2,
          borderDash: [5, 5],
          label: {
            display: true,
            content: 'Arch Formation',
            position: 'start',
            backgroundColor: 'rgba(255, 99, 132, 0.9)',
            color: 'white',
            font: { size: 11, weight: 'bold' },
          },
        };

        const handleEndIdx = 'breakdown' in pattern.key_points
          ? patternStartIndex + pattern.key_points.breakdown
          : patternEndIndex;

        annotations['handle_region'] = {
          type: 'box',
          xMin: archRightIdx,
          xMax: handleEndIdx,
          backgroundColor: 'rgba(255, 159, 64, 0.15)',
          borderColor: 'rgba(255, 159, 64, 0.6)',
          borderWidth: 2,
          borderDash: [3, 3],
          label: {
            display: true,
            content: 'Handle',
            position: 'start',
            backgroundColor: 'rgba(255, 159, 64, 0.9)',
            color: 'white',
            font: { size: 11, weight: 'bold' },
          },
        };
      } else if (pattern.pattern_type === 'double_top' || pattern.pattern_type === 'double_bottom') {
        // Double top/bottom: Only show the two peaks or two bottoms (defining characteristic)
        // These patterns have 9 control points for DTW but only 2 should be visualized
        const keyPointsToShow = pattern.pattern_type === 'double_top'
          ? ['first_peak', 'second_peak']
          : ['first_bottom', 'second_bottom'];

        Object.entries(pattern.key_points).forEach(([pointName, relativeIndex]) => {
          if (!keyPointsToShow.includes(pointName)) return;
          const absoluteIndex = patternStartIndex + relativeIndex;
          if (absoluteIndex >= 0 && absoluteIndex < fullPriceData.length) {
            const label = keyPointLabels[pointName] || pointName.replace(/_/g, ' ');
            annotations[`keypoint_${pointName}`] = makePointAnnotation(pointName, absoluteIndex, label);
          }
        });
      } else if (pattern.pattern_type === 'head_shoulders' || pattern.pattern_type === 'inv_head_shoulders') {
        // Head & Shoulders: Only show 7 structural points, skip intermediate DTW control points
        // The template has 11 points but 4 are intermediate (_ascent/_descent) for DTW matching
        const structuralPoints = pattern.pattern_type === 'head_shoulders'
          ? ['initial_low', 'left_shoulder', 'left_trough', 'head', 'right_trough', 'right_shoulder', 'neckline_break']
          : ['initial_high', 'left_shoulder', 'left_peak', 'head', 'right_peak', 'right_shoulder', 'neckline_break'];

        Object.entries(pattern.key_points).forEach(([pointName, relativeIndex]) => {
          if (!structuralPoints.includes(pointName)) return;
          const absoluteIndex = patternStartIndex + relativeIndex;
          if (absoluteIndex >= 0 && absoluteIndex < fullPriceData.length) {
            const label = keyPointLabels[pointName] || pointName.replace(/_/g, ' ');
            annotations[`keypoint_${pointName}`] = makePointAnnotation(pointName, absoluteIndex, label);
          }
        });
      } else {
        // Standard point annotations for other patterns (triangles, etc.)
        Object.entries(pattern.key_points).forEach(([pointName, relativeIndex]) => {
          const absoluteIndex = patternStartIndex + relativeIndex;
          if (absoluteIndex >= 0 && absoluteIndex < fullPriceData.length) {
            const label = keyPointLabels[pointName] || pointName.replace(/_/g, ' ');
            annotations[`keypoint_${pointName}`] = makePointAnnotation(pointName, absoluteIndex, label);
          }
        });
      }
    }

    // Support/resistance overlay (PR.5) — a level's price relative to the LAST close is a
    // purely descriptive fact (is price currently above or below it), not a directional
    // call, so it's safe to color-code that way without implying a prediction.
    if (showSr && srData.length > 0 && fullPriceData.length > 0) {
      const lastClose = fullPriceData[fullPriceData.length - 1];
      srData.forEach((level, i) => {
        const isResistance = level.price > lastClose;
        const color = isResistance ? 'rgba(220,38,38,0.55)' : 'rgba(5,150,105,0.55)';
        annotations[`sr_${i}`] = {
          type: 'line',
          yMin: level.price,
          yMax: level.price,
          borderColor: color,
          borderWidth: 1,
          borderDash: [6, 3],
          label: {
            display: true,
            content: `$${level.price.toFixed(2)} · ${level.touches}×`,
            position: 'end',
            backgroundColor: color,
            color: 'white',
            font: { size: 9, weight: 'bold' as const },
            padding: 3,
          },
        };
      });
    }

    return annotations;
  };

  // Format dates for display (MM/DD)
  const formatDateLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  // Create datasets array starting with main price line
  const datasets: object[] = [
    {
      label: `${pattern.ticker} Price`,
      data: fullPriceData,
      borderColor: 'rgb(75, 192, 192)',
      backgroundColor: 'rgba(75, 192, 192, 0.1)',
      borderWidth: 2,
      fill: true,
      tension: 0.4,
      pointRadius: 0,
      pointHoverRadius: 6,
    },
  ];

  // Bollinger Bands overlay on main price chart
  if (showBbands && bbandsData) {
    datasets.push(
      { label: 'BB Upper', data: bbandsData.upper, borderColor: 'rgba(239,83,80,0.7)', backgroundColor: 'transparent', borderWidth: 1, borderDash: [4, 3], pointRadius: 0, tension: 0.3, spanGaps: false },
      { label: 'BB Middle', data: bbandsData.middle, borderColor: 'rgba(150,150,150,0.7)', backgroundColor: 'transparent', borderWidth: 1, borderDash: [4, 3], pointRadius: 0, tension: 0.3, spanGaps: false },
      { label: 'BB Lower', data: bbandsData.lower, borderColor: 'rgba(54,162,235,0.7)', backgroundColor: 'transparent', borderWidth: 1, borderDash: [4, 3], pointRadius: 0, tension: 0.3, spanGaps: false },
    );
  }

  // Note: Cup curve overlay removed - boxes provide cleaner visualization

  const chartData = {
    labels: priceDates.map(formatDateLabel),
    datasets: datasets,
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
      },
      title: {
        display: true,
        text: `${pattern.pattern_display_name || pattern.pattern_type} - ${pattern.ticker}`,
        font: {
          size: 18,
          weight: 'bold' as const,
        },
      },
      // Full security name under the "<Pattern> - <TICKER>" title.
      subtitle: {
        display: !!getTickerName(pattern.ticker),
        text: getTickerName(pattern.ticker) ?? '',
        color: 'rgba(130,130,130,0.9)',
        font: { size: 13 },
        padding: { bottom: 6 },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          title: (context: Array<{ dataIndex: number }>) => {
            // Show full date in tooltip
            const index = context[0].dataIndex;
            return priceDates[index] || '';
          },
        },
      },
      annotation: {
        annotations: getPatternAnnotations(),
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Date',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          autoSkip: true,
          maxTicksLimit: 10,
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Price ($)',
        },
      },
    },
  };

  const buildCandleChartData = () => {
    const candleDatasets: object[] = [{
      label: `${pattern!.ticker} Price`,
      data: fullPriceData.map(() => 0),
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      candleData: fullPriceData.map((close, i) => ({
        o: ohlcOpens[i] ?? null,
        h: ohlcHighs[i] ?? null,
        l: ohlcLows[i]  ?? null,
        c: close,
      })),
    }];
    if (showBbands && bbandsData) {
      candleDatasets.push(
        { type: 'line', label: 'BB Upper', data: bbandsData.upper, borderColor: 'rgba(239,83,80,0.7)', backgroundColor: 'transparent', borderWidth: 1, borderDash: [4, 3], pointRadius: 0, tension: 0.3, spanGaps: false },
        { type: 'line', label: 'BB Middle', data: bbandsData.middle, borderColor: 'rgba(150,150,150,0.7)', backgroundColor: 'transparent', borderWidth: 1, borderDash: [4, 3], pointRadius: 0, tension: 0.3, spanGaps: false },
        { type: 'line', label: 'BB Lower', data: bbandsData.lower, borderColor: 'rgba(54,162,235,0.7)', backgroundColor: 'transparent', borderWidth: 1, borderDash: [4, 3], pointRadius: 0, tension: 0.3, spanGaps: false },
      );
    }
    return { labels: priceDates.map(formatDateLabel), datasets: candleDatasets };
  };

  const buildCandleChartOptions = () => {
    const validHighs = ohlcHighs.filter((v): v is number => v !== null);
    const validLows  = ohlcLows.filter((v): v is number => v !== null);
    const yMax = validHighs.length > 0 ? Math.max(...validHighs) * 1.01 : undefined;
    const yMin = validLows.length  > 0 ? Math.min(...validLows)  * 0.99 : undefined;

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        candlestickRenderer: {},
        legend: {
          display: true,
          position: 'top' as const,
        },
        title: {
          display: true,
          text: `${pattern!.pattern_display_name || pattern!.pattern_type} - ${pattern!.ticker}`,
          font: {
            size: 18,
            weight: 'bold' as const,
          },
        },
        // Full security name under the "<Pattern> - <TICKER>" title.
        subtitle: {
          display: !!getTickerName(pattern!.ticker),
          text: getTickerName(pattern!.ticker) ?? '',
          color: 'rgba(130,130,130,0.9)',
          font: { size: 13 },
          padding: { bottom: 6 },
        },
        tooltip: {
          mode: 'index' as const,
          intersect: false,
          callbacks: {
            title: (context: Array<{ dataIndex: number }>) => {
              const index = context[0].dataIndex;
              return priceDates[index] || '';
            },
            label: (context: { dataIndex: number }) => {
              const i = context.dataIndex;
              const o = ohlcOpens[i];
              const h = ohlcHighs[i];
              const l = ohlcLows[i];
              const c = fullPriceData[i];
              if (o === null || h === null || l === null) return `Close: ${c.toFixed(2)}`;
              return `O: ${o.toFixed(2)}  H: ${h.toFixed(2)}  L: ${l.toFixed(2)}  C: ${c.toFixed(2)}`;
            },
          },
        },
        annotation: {
          annotations: getPatternAnnotations(),
        },
      },
      scales: {
        x: {
          display: true,
          title: { display: true, text: 'Date' },
          ticks: {
            maxRotation: 45,
            minRotation: 45,
            autoSkip: true,
            maxTicksLimit: 10,
          },
        },
        y: {
          display: true,
          title: { display: true, text: 'Price ($)' },
          ...(yMin !== undefined ? { min: yMin } : {}),
          ...(yMax !== undefined ? { max: yMax } : {}),
        },
      },
    };
  };

  const buildRsiChartData = () => ({
    labels: priceDates.map(formatDateLabel),
    datasets: [{
      label: 'RSI(14)',
      data: rsiData,
      borderColor: 'rgb(153, 102, 255)',
      backgroundColor: 'transparent',
      borderWidth: 1.5,
      pointRadius: 0,
      pointHoverRadius: 4,
      tension: 0.3,
      spanGaps: false,
    }],
  });

  const buildRsiChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'index' as const, intersect: false },
      annotation: {
        annotations: {
          overbought: { type: 'line' as const, yMin: 70, yMax: 70, borderColor: 'rgba(239,83,80,0.6)', borderWidth: 1, borderDash: [4, 4] },
          oversold:   { type: 'line' as const, yMin: 30, yMax: 30, borderColor: 'rgba(38,166,154,0.6)', borderWidth: 1, borderDash: [4, 4] },
          midline:    { type: 'line' as const, yMin: 50, yMax: 50, borderColor: 'rgba(150,150,150,0.3)', borderWidth: 1, borderDash: [2, 4] },
        },
      },
    },
    scales: {
      x: { display: true, ticks: { autoSkip: true, maxTicksLimit: 10, maxRotation: 45 } },
      y: { display: true, min: 0, max: 100, ticks: { stepSize: 20 } },
    },
  });

  const buildMacdChartData = () => {
    const hist = macdData?.histogram ?? [];
    return {
      labels: priceDates.map(formatDateLabel),
      datasets: [
        {
          type: 'bar' as const,
          label: 'Histogram',
          data: hist,
          backgroundColor: hist.map(v => v === null ? 'transparent' : v >= 0 ? 'rgba(38,166,154,0.5)' : 'rgba(239,83,80,0.5)'),
          borderWidth: 0,
          barPercentage: 0.9,
        },
        {
          type: 'line' as const,
          label: 'MACD',
          data: macdData?.macd ?? [],
          borderColor: 'rgb(53,162,235)',
          backgroundColor: 'transparent',
          borderWidth: 1.5,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.3,
          spanGaps: false,
        },
        {
          type: 'line' as const,
          label: 'Signal',
          data: macdData?.signal ?? [],
          borderColor: 'rgb(255,159,64)',
          backgroundColor: 'transparent',
          borderWidth: 1.5,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.3,
          spanGaps: false,
        },
      ],
    };
  };

  const buildMacdChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    plugins: {
      legend: { display: true, position: 'top' as const, labels: { boxWidth: 12, font: { size: 10 } } },
      tooltip: { mode: 'index' as const, intersect: false },
      annotation: {
        annotations: {
          zeroline: { type: 'line' as const, yMin: 0, yMax: 0, borderColor: 'rgba(150,150,150,0.4)', borderWidth: 1 },
        },
      },
    },
    scales: {
      x: { display: true, ticks: { autoSkip: true, maxTicksLimit: 10, maxRotation: 45 } },
      y: { display: true },
    },
  });

  const buildStochChartData = () => ({
    labels: priceDates.map(formatDateLabel),
    datasets: [
      {
        label: '%K',
        data: stochData?.k ?? [],
        borderColor: 'rgb(53,162,235)',
        backgroundColor: 'transparent',
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.3,
        spanGaps: false,
      },
      {
        label: '%D',
        data: stochData?.d ?? [],
        borderColor: 'rgb(255,159,64)',
        backgroundColor: 'transparent',
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.3,
        spanGaps: false,
      },
    ],
  });

  const buildStochChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    plugins: {
      legend: { display: true, position: 'top' as const, labels: { boxWidth: 12, font: { size: 10 } } },
      tooltip: { mode: 'index' as const, intersect: false },
      annotation: {
        annotations: {
          overbought: { type: 'line' as const, yMin: 80, yMax: 80, borderColor: 'rgba(239,83,80,0.6)', borderWidth: 1, borderDash: [4, 4] },
          oversold:   { type: 'line' as const, yMin: 20, yMax: 20, borderColor: 'rgba(38,166,154,0.6)', borderWidth: 1, borderDash: [4, 4] },
        },
      },
    },
    scales: {
      x: { display: true, ticks: { autoSkip: true, maxTicksLimit: 10, maxRotation: 45 } },
      y: { display: true, min: 0, max: 100, ticks: { stepSize: 20 } },
    },
  });

  const buildVolumeChartData = () => ({
    labels: priceDates.map(formatDateLabel),
    datasets: [{
      type: 'bar' as const,
      label: 'Volume',
      data: volumeData,
      backgroundColor: fullPriceData.map((close, i) => {
        const prev = fullPriceData[i - 1];
        return i === 0 || close >= prev ? 'rgba(38,166,154,0.5)' : 'rgba(239,83,80,0.5)';
      }),
      borderWidth: 0,
      barPercentage: 0.9,
    }],
  });

  const buildVolumeChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: (ctx: any) => {
            const v = ctx.parsed.y as number;
            if (v >= 1_000_000) return `Vol: ${(v / 1_000_000).toFixed(2)}M`;
            if (v >= 1_000) return `Vol: ${(v / 1_000).toFixed(0)}K`;
            return `Vol: ${v}`;
          },
        },
      },
      annotation: { annotations: {} },
    },
    scales: {
      x: { display: true, ticks: { autoSkip: true, maxTicksLimit: 10, maxRotation: 45 } },
      y: {
        display: true,
        ticks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          callback: (v: any) => {
            const n = v as number;
            if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
            if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
            return `${n}`;
          },
        },
      },
    },
  });

  const getSentimentIcon = () => {
    if (pattern.sentiment === 'bullish') {
      return <FaArrowUp className="sentiment-icon bullish" />;
    } else if (pattern.sentiment === 'bearish') {
      return <FaArrowDown className="sentiment-icon bearish" />;
    }
    return null;
  };

  const confidencePct = (pattern.confidence * 100).toFixed(1);

  return (
    <div className="pattern-detail-page">
      <Container>
        <Row className="mb-3">
          <Col>
            <Button color="link" onClick={() => navigate(getBackUrl())} className="back-button">
              <FaArrowLeft /> Back to Patterns
            </Button>
          </Col>
        </Row>

        <Row>
          <Col lg="8">
            <Card className="pattern-chart-card">
              <CardBody>
                <div className="chart-mode-toggle">
                  <Button size="sm" color={chartMode === 'line' ? 'primary' : 'outline-secondary'} onClick={() => setChartMode('line')}>
                    Line
                  </Button>{' '}
                  <Button size="sm" color={chartMode === 'candle' ? 'primary' : 'outline-secondary'} onClick={() => setChartMode('candle')} disabled={!candleDataAvailable}>
                    Candle
                  </Button>
                  {(rsiData.length > 0 || macdData || bbandsData || stochData || volumeData.some(v => v !== null)) && (
                    <div className="indicator-dropdown-wrap" ref={indicatorDropdownRef}>
                      <span className="toggle-divider">|</span>
                      <button
                        className={`indicator-dropdown-btn${indicatorDropdownOpen ? ' open' : ''}`}
                        onClick={() => setIndicatorDropdownOpen(v => !v)}
                      >
                        Indicators
                        {[showRsi, showMacd, showBbands, showStoch, showVolume, showSr].filter(Boolean).length > 0 && (
                          <span className="indicator-active-count">
                            {[showRsi, showMacd, showBbands, showStoch, showVolume, showSr].filter(Boolean).length}
                          </span>
                        )}
                        <span className="indicator-chevron">{indicatorDropdownOpen ? '▲' : '▼'}</span>
                      </button>
                      {indicatorDropdownOpen && (
                        <div className="indicator-dropdown-menu">
                          {rsiData.length > 0 && (
                            <label className="indicator-checkbox-row">
                              <input type="checkbox" checked={showRsi} onChange={() => setShowRsi(v => !v)} />
                              <span>RSI <small>(14)</small></span>
                            </label>
                          )}
                          {macdData && (
                            <label className="indicator-checkbox-row">
                              <input type="checkbox" checked={showMacd} onChange={() => setShowMacd(v => !v)} />
                              <span>MACD <small>(12,26,9)</small></span>
                            </label>
                          )}
                          {bbandsData && (
                            <label className="indicator-checkbox-row">
                              <input type="checkbox" checked={showBbands} onChange={() => setShowBbands(v => !v)} />
                              <span>Bollinger Bands <small>(20,2σ)</small></span>
                            </label>
                          )}
                          {stochData && (
                            <label className="indicator-checkbox-row">
                              <input type="checkbox" checked={showStoch} onChange={() => setShowStoch(v => !v)} />
                              <span>Stochastic <small>(14,3)</small></span>
                            </label>
                          )}
                          {volumeData.some(v => v !== null) && (
                            <label className="indicator-checkbox-row">
                              <input type="checkbox" checked={showVolume} onChange={() => setShowVolume(v => !v)} />
                              <span>Volume</span>
                            </label>
                          )}
                          {srData.length > 0 && (
                            <label className="indicator-checkbox-row">
                              <input type="checkbox" checked={showSr} onChange={() => setShowSr(v => !v)} />
                              <span>Support/Resistance</span>
                            </label>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ height: '370px' }}>
                  {chartMode === 'line' ? (
                    <Line ref={mainChartRef} data={chartData as never} options={chartOptions as never} plugins={[syncHoverPlugin]} />
                  ) : (
                    <Chart ref={mainChartRef} type="bar" data={buildCandleChartData() as never} options={buildCandleChartOptions() as never} plugins={[candlestickPlugin, syncHoverPlugin]} />
                  )}
                </div>
                {showVolume && volumeData.some(v => v !== null) && (
                  <div className="indicator-panel">
                    <span className="indicator-panel-label">Volume</span>
                    <Chart ref={volumeChartRef} type="bar" data={buildVolumeChartData() as never} options={buildVolumeChartOptions() as never} plugins={[syncHoverPlugin]} />
                  </div>
                )}
                {showRsi && rsiData.length > 0 && !rsiLoading && (
                  <div className="indicator-panel">
                    <span className="indicator-panel-label">RSI(14)</span>
                    <Line ref={rsiChartRef} data={buildRsiChartData()} options={buildRsiChartOptions() as never} plugins={[syncHoverPlugin]} />
                  </div>
                )}
                {showMacd && macdData && (
                  <div className="indicator-panel">
                    <span className="indicator-panel-label">MACD(12,26,9)</span>
                    <Chart ref={macdChartRef} type="bar" data={buildMacdChartData() as never} options={buildMacdChartOptions() as never} plugins={[syncHoverPlugin]} />
                  </div>
                )}
                {showStoch && stochData && (
                  <div className="indicator-panel">
                    <span className="indicator-panel-label">Stochastic(14,3)</span>
                    <Line ref={stochChartRef} data={buildStochChartData()} options={buildStochChartOptions() as never} plugins={[syncHoverPlugin]} />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col lg="4">
            <Card className="pattern-info-card">
              <CardBody>
                <h3>
                  {pattern.pattern_display_name || pattern.pattern_type}
                  {getSentimentIcon()}
                </h3>

                <ShareButton
                  color="primary"
                  outline
                  size="sm"
                  label="Share"
                  className="mb-3"
                  title={`${pattern.ticker} — ${pattern.pattern_display_name || pattern.pattern_type} | DejaChart`}
                  path={`/api/share/pattern/${pattern.ticker}?type=${pattern.pattern_type}&window=${pattern.window}&start=${pattern.start_date}`}
                />

                <div className="pattern-stats">
                  <div className="stat-item">
                    <span className="stat-label">Security</span>
                    <span className="stat-value">{getTickerName(pattern.ticker) ?? pattern.ticker}</span>
                  </div>

                  <div className="stat-item">
                    <span className="stat-label">Confidence</span>
                    <span className="stat-value">
                      {confidencePct}%
                      <div className="confidence-bar-mini">
                        <div
                          className="confidence-fill"
                          style={{ width: `${confidencePct}%` }}
                        />
                      </div>
                    </span>
                  </div>

                  <div className="stat-item">
                    <span className="stat-label">Length</span>
                    <span className="stat-value">
                      {pattern.price_data ? pattern.price_data.length : pattern.window} days
                    </span>
                  </div>

                  <div className="stat-item">
                    <span className="stat-label">Period</span>
                    <span className="stat-value">
                      {pattern.start_date}
                      <br />
                      to {pattern.end_date}
                    </span>
                  </div>

                  <div className="stat-item">
                    <span className="stat-label">Sentiment</span>
                    <Badge color={pattern.sentiment === 'bullish' ? 'success' : 'danger'}>
                      {pattern.sentiment?.toUpperCase()}
                    </Badge>
                  </div>

                  <div className="stat-item">
                    <span className="stat-label">Earnings</span>
                    <span className="stat-value">
                      <EarningsBadge ticker={pattern.ticker} />
                    </span>
                  </div>

                  {(() => {
                    const f = getFundamentals(pattern.ticker);
                    if (!f) return null;
                    return (
                      <>
                        {f.sector && (
                          <div className="stat-item">
                            <span className="stat-label">Sector</span>
                            <span className="stat-value">{f.sector}</span>
                          </div>
                        )}
                        {f.industry && (
                          <div className="stat-item">
                            <span className="stat-label">Industry</span>
                            <span className="stat-value" style={{ fontSize: 12 }}>{f.industry}</span>
                          </div>
                        )}
                        {f.market_cap && (
                          <div className="stat-item">
                            <span className="stat-label">Market Cap</span>
                            <span className="stat-value">{formatMarketCap(f.market_cap)}</span>
                          </div>
                        )}
                      </>
                    );
                  })()}

                </div>

                <>
                  <hr />
                  <div className="pattern-about">
                    <div className="pattern-about-header">
                      <h6>About this Pattern</h6>
                      <PatternSparkline patternType={pattern.pattern_type} width={90} height={44} className="pattern-about-sparkline" />
                    </div>
                    <p className="pattern-about-summary">{PATTERN_INFO[pattern.pattern_type].summary}</p>
                    <p className="text-muted pattern-about-details"><small>{PATTERN_INFO[pattern.pattern_type].details}</small></p>
                  </div>
                </>

                <hr />

                <div className="pattern-actions">
                  <Button
                    color="primary"
                    block
                    onClick={() => {
                      const endDate = new Date(pattern.end_date);
                      const dateStr = endDate.toISOString().split('T')[0];
                      navigate(buildDashboardUrl({ ticker: pattern.ticker, date: dateStr, window: pattern.window, auto: true }));
                    }}
                  >
                    Query Similar Patterns
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
